import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { OPENAI_SORA_SIZE_MAP } from "../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const sora2Params = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16"]).default("9:16"),
    resolution: z.enum(["720p"]).default("720p"),
    attachments: z.array(attachment).optional(),
    duration: z.enum(["4", "8", "12", "16", "20"]).default("8"),
    prompt: z.string().default(""),
    characterIds: z.array(z.string()).optional(),
  })
  .strip();

export const sora2 = defineModel({
  key: "sora2",
  stepType: "video",
  spec: {
    label: "Sora 2",
    description: "Good for highly realistic people, action scenes and anime",
    icon: "sora-icon.svg",
    featured: true,
    tags: ["video-gen"],
    info: "Generating a video with this model takes between 5-10 minutes.",
    features: {
      aspectRatio: true,
      duration: true,
      resolution: true,
      characters: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/webp", "image/png"],
          label: "Input Image",
          slot: "input_image",
          group: "reference",
          required: false,
        },
      ],
      aspectRatio: {
        items: [
          { label: "9:16", value: "9:16" },
          { label: "16:9", value: "16:9" },
        ],
      },
      resolution: {
        items: [{ label: "720p", value: "720p" }],
      },
      duration: {
        items: [
          { label: "4s", value: "4" },
          { label: "8s", value: "8" },
          { label: "12s", value: "12" },
          { label: "16s", value: "16" },
          { label: "20s", value: "20" },
        ],
      },
    },
    params: sora2Params,
  },
  referencePricing: {
    provider: "OpenAI",
    url: "https://openai.com/api/pricing",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 100_000 }],
  },
  providers: [
    {
      provider: "openai",
      modelId: "sora-2",
      estimateCost(params) {
        const costPerSecond = 0.1;
        return Math.ceil(
          1_000_000 *
            costPerSecond *
            Number(params.duration) *
            (1 + BRAND_MARKUP),
        );
      },
      costPerSecond: 0.1 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const inputImageUrl = params.attachments?.find(
          (attachment) => attachment.slot === "input_image",
        )?.data;
        const size =
          OPENAI_SORA_SIZE_MAP[params.resolution][params.aspectRatio];

        return {
          model: this.modelId,
          prompt: params.prompt,
          seconds: params.duration,
          size,
          ...(inputImageUrl
            ? { input_reference: { image_url: inputImageUrl } }
            : {}),
          ...(params.characterIds && params.characterIds.length > 0
            ? {
                characters: params.characterIds.map((id) => ({
                  id,
                })),
              }
            : {}),
        };
      },
    },
  ],
});
